import type { IConsumable } from '@experiences/interfaces';
import { UiText } from '@experiences/ui-common';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CONSUMABLES_WITH_CONSUMPTION_GRAPHS } from '../../../common/constants/Constant';
import { getDefaultConsumptionPool } from '../../../services/licensing/LicenseUsageService';
import { ConsumptionGraphGroup } from './ConsumptionGraphGroup';
import { TenantUsageGrid } from './TenantUsageGrid';

interface TenantsUnitOverviewUsageGroup {
    product: string;
    consumable: IConsumable;
    tenantId?: string;
}

const useStyles = makeStyles(theme =>
    createStyles({
        separator: {
            width: '100%',
            height: '2px',
            backgroundColor: theme.palette.semantic.colorBorderDeEmp,
            marginTop: '36px',
        },
        title: {
            fontSize: '14px',
            color: theme.palette.semantic.colorForeground,
            fontWeight: 600,
            marginTop: '16px',
            marginBottom: '7px',
        },
    }));

export const TenantsUnitsOverviewUsageGroup: React.FC<TenantsUnitOverviewUsageGroup> = ({
    product, consumable, tenantId,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const [ startDate, endDate ] = useMemo(() => getDefaultConsumptionPool(consumable), [ consumable ]);
    const showConsumptionGraph = CONSUMABLES_WITH_CONSUMPTION_GRAPHS.includes(product);

    return (
        <>
            {!tenantId && (
                <>
                    <div id="ui-grid-section">
                        <TenantUsageGrid
                            consumableCode={consumable.code}
                            tenantId={tenantId}
                            startDate={startDate}
                            endDate={endDate} />
                    </div>
                    {showConsumptionGraph && <div className={classes.separator} />}
                </>
            )}
            {showConsumptionGraph && (
                <div id="ui-chart-section">
                    <UiText
                        className={classes.title}
                        role="heading"
                        aria-level={3}>
                        {translate({ id: `CLIENT_CONSUMABLE_CHART_HEADER_${product}` },)}
                    </UiText>
                    <ConsumptionGraphGroup
                        product={product}
                        consumable={consumable}
                        tenantId={tenantId}
                        startDateParam={startDate}
                        endDateParam={endDate} />
                </div>
            )}
        </>
    );
};
